import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search, ShoppingCart, User, Menu, Phone, MapPin, LogOut, Package, LayoutDashboard, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import {
  Sheet, SheetContent, SheetTrigger,
} from "@/components/ui/sheet";

const Header = () => {
  const { count } = useCart();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [q, setQ] = useState("");

  useEffect(() => {
    api.get("/categories").then((res) => setCategories(res.data.categories || [])).catch(() => {});
  }, []);

  const submitSearch = (e) => {
    e.preventDefault();
    if (!q.trim()) return;
    navigate(`/shop?search=${encodeURIComponent(q.trim())}`);
  };

  return (
    <header className="sticky top-0 z-40 bg-white shadow-sm border-b border-slate-200">
      {/* Top strip */}
      <div className="bg-[#0b3d75] text-white text-xs">
        <div className="max-w-7xl mx-auto px-4 h-9 flex items-center justify-between">
          <div className="hidden md:flex items-center gap-4">
            <a href="tel:18882869244" className="flex items-center gap-1.5 hover:text-amber-300 transition-colors">
              <Phone className="w-3.5 h-3.5" /> 1 (888) 286-9244
            </a>
            <span className="flex items-center gap-1.5 text-slate-200">
              <MapPin className="w-3.5 h-3.5" /> 1871 Woodslee Dr, Troy, MI 48083
            </span>
          </div>
          <div className="flex items-center gap-4 ml-auto">
            <span className="hidden sm:block">Free shipping on orders $75+</span>
            <Link to="/account/orders" className="hover:text-amber-300">Track Order</Link>
            <Link to="/contact" className="hover:text-amber-300">Support</Link>
          </div>
        </div>
      </div>

      {/* Main bar */}
      <div className="max-w-7xl mx-auto px-4 py-3 flex items-center gap-4">
        {/* Mobile menu */}
        <Sheet>
          <SheetTrigger asChild>
            <Button variant="ghost" size="icon" className="md:hidden">
              <Menu className="w-5 h-5" />
            </Button>
          </SheetTrigger>
          <SheetContent side="left" className="w-80 overflow-y-auto">
            <div className="mt-6">
              <h3 className="font-semibold text-slate-900 mb-3">Shop by Category</h3>
              <ul className="space-y-2">
                {categories.map((c) => (
                  <li key={c.slug}>
                    <Link to={`/shop/${c.slug}`} className="block px-3 py-2 rounded-md hover:bg-slate-100 text-slate-700">
                      {c.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </SheetContent>
        </Sheet>

        {/* Logo */}
        <Link to="/" className="flex items-center gap-2 shrink-0">
          <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#0b3d75] to-[#1266b8] flex items-center justify-center shadow">
            <span className="text-white font-black text-lg tracking-tight">iZ</span>
          </div>
          <div className="leading-tight hidden sm:block">
            <div className="font-black text-[#0b3d75] text-lg tracking-tight">IZZI MEDICAL</div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500">Supplies</div>
          </div>
        </Link>

        {/* Search */}
        <form onSubmit={submitSearch} className="flex-1 max-w-2xl">
          <div className="relative">
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search 10,000+ medical supplies..."
              className="pl-10 pr-24 h-11 border-slate-300 focus-visible:ring-[#0b3d75]"
            />
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Button type="submit" className="absolute right-1 top-1 h-9 bg-[#0b3d75] hover:bg-[#0a4e94] text-white">
              Search
            </Button>
          </div>
        </form>

        {/* Right actions */}
        <div className="flex items-center gap-1">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="h-11 gap-2 text-slate-700 hover:text-[#0b3d75]">
                <User className="w-5 h-5" />
                <span className="hidden lg:inline text-sm font-medium">
                  {user ? user.first_name : "Account"}
                </span>
                <ChevronDown className="w-3.5 h-3.5 hidden lg:inline" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              {user ? (
                <>
                  <DropdownMenuLabel className="font-normal">
                    <div className="text-xs text-slate-500">Signed in as</div>
                    <div className="font-medium text-slate-900 truncate">{user.email}</div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate("/account/orders")}>
                    <Package className="w-4 h-4 mr-2" /> My Orders
                  </DropdownMenuItem>
                  {user.role === "admin" && (
                    <DropdownMenuItem onClick={() => navigate("/admin")}>
                      <LayoutDashboard className="w-4 h-4 mr-2" /> Admin Dashboard
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => { logout(); navigate("/"); }}>
                    <LogOut className="w-4 h-4 mr-2" /> Sign Out
                  </DropdownMenuItem>
                </>
              ) : (
                <>
                  <DropdownMenuItem onClick={() => navigate("/login")}>Sign In</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/register")}>Create Account</DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>

          <Button asChild variant="ghost" className="h-11 relative gap-2 text-slate-700 hover:text-[#0b3d75]">
            <Link to="/cart">
              <div className="relative">
                <ShoppingCart className="w-5 h-5" />
                {count > 0 && (
                  <Badge className="absolute -top-2 -right-2 h-5 min-w-5 rounded-full bg-amber-500 hover:bg-amber-500 text-white p-0 flex items-center justify-center text-[10px] font-bold">
                    {count}
                  </Badge>
                )}
              </div>
              <span className="hidden lg:inline text-sm font-medium">Cart</span>
            </Link>
          </Button>
        </div>
      </div>

      {/* Category strip */}
      <nav className="border-t border-slate-200 bg-slate-50 hidden md:block">
        <div className="max-w-7xl mx-auto px-4">
          <ul className="flex items-center gap-1 overflow-x-auto">
            <li>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="flex items-center gap-2 px-3 py-2.5 text-sm font-semibold text-[#0b3d75] hover:bg-slate-100 rounded-md">
                    <Menu className="w-4 h-4" /> All Categories
                    <ChevronDown className="w-3.5 h-3.5" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-64">
                  {categories.map((c) => (
                    <DropdownMenuItem key={c.slug} onClick={() => navigate(`/shop/${c.slug}`)}>
                      {c.name}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </li>
            {categories.slice(0, 8).map((c) => (
              <li key={c.slug}>
                <Link
                  to={`/shop/${c.slug}`}
                  className="whitespace-nowrap px-3 py-2.5 text-sm text-slate-700 hover:text-[#0b3d75] hover:bg-slate-100 rounded-md font-medium block"
                >
                  {c.name}
                </Link>
              </li>
            ))}
            <li className="ml-auto">
              <Link to="/shop" className="whitespace-nowrap px-3 py-2.5 text-sm text-amber-700 hover:text-amber-800 font-semibold">
                Shop All Products →
              </Link>
            </li>
          </ul>
        </div>
      </nav>
    </header>
  );
};

export default Header;
